import AddIcon from '@mui/icons-material/Add';
import { Stack, Typography, Button, Divider, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import DayCheckBox from './weeklyHours/dayCheckbox';


import { axiosInstance } from '../../../../assets/utils/axiosInstance';
import { WEEKDAYS } from '../../../../constants/meetings';
import { useToastNotificationContext } from '../../../../hooks/global/alert/useToastNotification';
import { IntervalsObject } from '../../../../interfaces/meeting';
import { RootState } from '../../../../utils/store';
import { ScheduleDataObject } from '../calendar/editAvailability';
import IntervalComponent from '../common/editAvailability/setIntervalComponent';
import { convertTimeRangeHourFormat, getValueToPropertyName } from '../common/helpers';

interface WeeklyHoursProps {
  isLoading: boolean;
  error: boolean;
  scheduleData: ScheduleDataObject;
  setScheduleData: React.Dispatch<React.SetStateAction<ScheduleDataObject>>;
}
interface AvailabilityDays {
  day: number;
  isChecked: boolean;
}

const styles = {
  container1: {
    padding: '24px 20px',
  }, 
  headerText: {
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 500,
    fontSize: '18px',
  },
  bodyText1: {
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 500,
    fontSize: '14px',
    color: '#878787',
  },
  bodyTextStyle2: {
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 400,
    fontSize: '16px',
  },
  unavailable: {
    border: '1px solid #E3E3E3',
    width: '100%',
    padding: '16px 8px',
    borderRadius: '6px',
  },
  buttonStyle1: {
    textTransform: 'none',
    bgcolor: '#2449FC',
    borderRadius: '50px',
    color: '#FFFFFF',
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 500,
    fontSize: '14px',
    padding: '5px 16px',
    maxHeight: '40px',
    '&:hover': {
      bgcolor: '#4E7AFF',
      color: '#FFFFFF',
    },
  },
  buttonStyle2: {
    textTransform: 'none',
    bgcolor: 'transparent',
    color: '#00CECF',
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 500,
    fontSize: '14px',
    padding: '5px 16px',
    '&:hover': {
      bgcolor: 'transparent',
      color: '#00CECF',
    },
  },
};


const WeeklyHours = ({ isLoading, error, scheduleData, setScheduleData }: WeeklyHoursProps) => {
  const intl = useIntl();
  const tenantId = useSelector((state: RootState) => state.meetings.tenantId);
  const { createToastNotification } = useToastNotificationContext();
  const [requestLoading, setRequestLoading] = useState<boolean>(false);
  const canAddInterval = useSelector(
    (state: RootState) => state.meetings.canAddInterval,
  );
  const [days, setDay] = useState<AvailabilityDays[]>([
    {
      day: WEEKDAYS.SUNDAY,
      isChecked: false,
    },
    {
      day: WEEKDAYS.MONDAY,
      isChecked: true,
    },
    {
      day: WEEKDAYS.TUESDAY,
      isChecked: true,
    },
    {
      day: WEEKDAYS.WEDNESDAY,
      isChecked: true,
    },
    {
      day: WEEKDAYS.THURSDAY,
      isChecked: true,
    },
    {
      day: WEEKDAYS.FRIDAY,
      isChecked: true,
    },
    {
      day: WEEKDAYS.SATURDAY,
      isChecked: false,
    },
  ]);

  useEffect(() => {
    setDay((weekday) => weekday.map(({ day })=> ({
      day: day,
      isChecked: scheduleData?.weekdays?.includes(day) ?? false,
    })));
  }, [scheduleData?.weekdays]);

  // change value when checkbox is toggled for weekdays
  const toggleDayHandler = (selected: number, value: boolean) => {
    const newArray = [...days];
    const itemindex = newArray.findIndex(day => day.day === selected);
    newArray[itemindex].isChecked = !value;
    setDay(newArray);
  };

  //add new item to timeslot intervals
  const addIntervalhandler = () => {
    setScheduleData((prevData) => ({ ...prevData, timeSlots: [
      ...prevData.timeSlots,
      {
        from: '',
        to: '',
      },
    ] }));
  };

  //remove item from timeslot intervals 
  const removeIntervalhandler = (index: number) => {
    const newIntervals: IntervalsObject[] = [...scheduleData.timeSlots];
    if (newIntervals[index] && newIntervals[index]?.id) {
      newIntervals[index].is_delete = true;
    } else {
      newIntervals.splice(index, 1);
    }
    setScheduleData((prevData) => ({ ...prevData, timeSlots: [...newIntervals] }));
  };

  //set timeslot interval base on index
  const setIntervalValues = (index: number, key: string, value: string) => {
    const newIntervals: IntervalsObject[] = [...scheduleData.timeSlots];
    if (index >= 0 && newIntervals.length > index) {
      if (key === 'from')
        newIntervals[index].from = value;
      if (key === 'to')
        newIntervals[index].to = value;
    }
    setScheduleData((prevData) => ({ ...prevData, timeSlots: [...newIntervals] }));
  };

  //check values before sending to server
  const isValuesValid = (): boolean => {
    if (!scheduleData.duration || scheduleData.duration < 10) {
      createToastNotification(intl.formatMessage({ defaultMessage: 'Duration cannot be empty or below 10 mins' }), 'error');
      return false;
    } 
    if (scheduleData.timeSlots.find(({ from, to, is_delete }) => !from && !to && !is_delete)) {
      createToastNotification(intl.formatMessage({ defaultMessage: 'Time slot cannot have empty fields' }), 'error');
      return false;
    }
    return true;
  };

  //submit request to save changes to BE
  const scheduleAvailabilityOnWeek = async () => {
    if (!isValuesValid()) return;
    setRequestLoading(true);
    if (!scheduleData?.id) {
      try {
        const res = await axiosInstance().post('schedule/availability', {
          date_start: new Date('2024-03-20T08:00:00').toISOString(),
          date_end: new Date('2024-03-20T08:00:00').toISOString(),
          duration: scheduleData.duration,
          tenant: tenantId,
          duration_type: '1',
          time_slots: convertTimeRangeHourFormat(scheduleData.timeSlots),
          weekdays: days.filter(day => day.isChecked).map(day => day.day),
        });
        if (res) {
          createToastNotification(intl.formatMessage({ defaultMessage: 'Availability Schedule Saved.' }), 'success');
        }
      } catch (err) {
        createToastNotification(intl.formatMessage({ defaultMessage: 'Something went wrong.' }), 'error');
      }
    } else if (scheduleData?.id) {
      try {
        const res = await axiosInstance().patch(`schedule/availability/${scheduleData?.id}`, {
          date_start: scheduleData.dateStart,
          date_end: scheduleData.dateEnd,
          duration: scheduleData.duration,
          tenant: scheduleData.tenant,
          duration_type: scheduleData.durationType,
          time_slots: convertTimeRangeHourFormat(scheduleData.timeSlots),
          weekdays: days.filter(day => day.isChecked).map(day => day.day),
        });
        if (res) {
          createToastNotification(intl.formatMessage({ defaultMessage: 'Availability Schedule Saved.' }), 'success');
        }
      } catch (err) {
        createToastNotification(intl.formatMessage({ defaultMessage: 'Something went wrong.' }), 'error');
      }
    }
    setRequestLoading(false);

  };

  return (
    <Stack gap= {2} sx={styles.container1}>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography sx={styles.headerText}>
          <FormattedMessage defaultMessage='Weekly hours' />
        </Typography>
        <Button 
          sx={styles.buttonStyle1}
          onClick={scheduleAvailabilityOnWeek}
          disabled={isLoading || error || !scheduleData?.duration}
        >
          {requestLoading && <CircularProgress size={20} sx={{ color: '#ffffff' }}/>}
          {!requestLoading && <FormattedMessage defaultMessage='Apply' />}
        </Button>
      </Stack>
      <Stack>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography sx={styles.bodyText1}>
            <FormattedMessage defaultMessage='Applies to' />
          </Typography>
          <Typography sx={styles.bodyText1}>
            <FormattedMessage defaultMessage='Hours available' />  
          </Typography>
          <Button 
            variant='text'
            sx={styles.buttonStyle2}
            startIcon={<AddIcon sx={{ color: canAddInterval ? '#00CECF' : '#878787' }}/>}
            onClick={addIntervalhandler}
            disabled={!scheduleData?.duration || !canAddInterval}
          >
            <FormattedMessage defaultMessage='New interval' />
          </Button>
        </Stack>
        <Divider />
        <Stack direction='row' paddingY='20px'>
          <Stack flexBasis='30%'>
            {days.map( ({ day, isChecked }: AvailabilityDays, index: number) => (
              <DayCheckBox 
                key={index}
                label={getValueToPropertyName(day)}
                checked={isChecked}
                disabled={scheduleData.duration === 0}
                onChange={()=>{toggleDayHandler(day, isChecked);}}
              />
            ))}
          </Stack>
          <Stack flexGrow={1}>
            {scheduleData?.timeSlots?.length === 0 && 
              <Stack alignItems='center' sx={styles.unavailable}>
                <Typography sx={{
                  ...styles.bodyTextStyle2,
                  color:'#878787',
                }}>
                  <FormattedMessage defaultMessage='Unavailable' />
                </Typography>
              </Stack>
            }
            {scheduleData?.timeSlots?.length > 0 && 
              <Stack direction='row' justifyContent='space-around'>
                <Stack flexBasis='45%'>
                  <Typography sx={styles.bodyTextStyle2}>
                    <FormattedMessage defaultMessage='From' />
                  </Typography>
                </Stack>
                <Stack flexBasis='55%'>
                  <Typography sx={styles.bodyTextStyle2}>
                    <FormattedMessage defaultMessage='To' />
                  </Typography>
                </Stack>
              </Stack>
            }
            <Stack gap={1}>
              {scheduleData?.timeSlots && scheduleData?.timeSlots?.map((interval, key) => (
                <IntervalComponent 
                  intervals={scheduleData?.timeSlots}
                  from={interval.from}
                  to={interval.to}
                  isDeleted={interval.is_delete}
                  setFrom={(e) => setIntervalValues(key, 'from', e)}
                  setTo={(e)=> setIntervalValues(key, 'to', e)}
                  index={key}
                  remove={removeIntervalhandler}
                  key={key}
                  duration={scheduleData.duration ?? 0}/>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default WeeklyHours;